import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { uploadAPI } from './services/api';
import Header from './components/Header';
import Loading from './components/Loading';
import CookieNotice from './components/CookieNotice';

// Pages
import Home from './pages/Home';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import LocationLanding from './pages/LocationLanding';
import RoleLanding from './pages/RoleLanding';
import SkillLanding from './pages/SkillLanding';
import CategoryParentLanding from './pages/CategoryParentLanding';
import CategoryChildLanding from './pages/CategoryChildLanding';
import CompanyProfile from './pages/CompanyProfile';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailRequired from './pages/VerifyEmailRequired';
import Help from './pages/Help';
import Pricing from './pages/Pricing';
import TalkToSales from './pages/TalkToSales';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';

// Static imports for the shell + public/auth pages (those need to be in the
// initial bundle for fast first paint and SEO). The static page imports above this
// stay non-lazy because they're hit on first-paint by bots and direct-link users.
import Sidebar from './components/Sidebar';
import PortalSidebar from './components/portalv2/PortalSidebar';
import PortalHeader from './components/portalv2/PortalHeader';

// Admin Pages (lazy — only loaded when an admin session navigates there;
// keeps the public/SEO bundle small and improves Core Web Vitals)
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const JobManagement = React.lazy(() => import('./pages/admin/JobManagement'));
const AdminProfile = React.lazy(() => import('./pages/admin/AdminProfile'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUserDetail = React.lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminJobDetail = React.lazy(() => import('./pages/admin/AdminJobDetail'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminBilling = React.lazy(() => import('./pages/admin/AdminBilling'));
const AdminCouponManagement = React.lazy(() => import('./pages/admin/AdminCouponManagement'));
const AdminBillingPartners = React.lazy(() => import('./pages/admin/AdminBillingPartners'));
const AdminBillingDashboard = React.lazy(() => import('./pages/admin/AdminBillingDashboard'));
const AdminBillingInvoices = React.lazy(() => import('./pages/admin/AdminBillingInvoices'));
const AdminPaymentGateway = React.lazy(() => import('./pages/admin/AdminPaymentGateway'));
const AdminPaymentMappings = React.lazy(() => import('./pages/admin/AdminPaymentMappings'));
const AdminPricingControls = React.lazy(() => import('./pages/admin/AdminPricingControls'));
const AdminRecruiterExceptions = React.lazy(() => import('./pages/admin/AdminRecruiterExceptions'));
const AdminApprovedPartnerProfiles = React.lazy(() => import('./pages/admin/AdminApprovedPartnerProfiles'));
const AdminPartnerUploads = React.lazy(() => import('./pages/admin/AdminPartnerUploads'));
const AdminAffiliateReferrals = React.lazy(() => import('./pages/admin/AdminAffiliateReferrals'));
const AdminKycReviews = React.lazy(() => import('./pages/admin/AdminKycReviews'));
const AdminActivity = React.lazy(() => import('./pages/admin/AdminActivity'));
const AdminSupport = React.lazy(() => import('./pages/admin/AdminSupport'));
const JDReviewList = React.lazy(() => import('./pages/admin/JDReviewList'));
const JDReviewDetail = React.lazy(() => import('./pages/admin/JDReviewDetail'));
const JDReviewDashboard = React.lazy(() => import('./pages/admin/JDReviewDashboard'));
const AdminOrgCaps = React.lazy(() => import('./pages/admin/AdminOrgCaps'));
const AdminOrgCapSettings = React.lazy(() => import('./pages/admin/AdminOrgCapSettings'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const AdminOrgAdmins = React.lazy(() => import('./pages/admin/AdminOrgAdmins'));
const RecruiterOrgMembers = React.lazy(() => import('./pages/recruiter/OrgMembers'));
const RecruiterOrgInvites = React.lazy(() => import('./pages/recruiter/OrgInvites'));
const RecruiterOrgJobs = React.lazy(() => import('./pages/recruiter/OrgJobs'));
const OrgPartnerAllowlist = React.lazy(() => import('./pages/recruiter/OrgPartnerAllowlist'));
const RecruiterDelegatedJobs = React.lazy(() => import('./pages/recruiter/RecruiterDelegatedJobs'));
const RecruiterDelegatedJobSubmissions = React.lazy(() => import('./pages/recruiter/RecruiterDelegatedJobSubmissions'));
const PartnerDelegatedJobs = React.lazy(() => import('./pages/partner/PartnerDelegatedJobs'));
const PartnerDelegatedJobDetail = React.lazy(() => import('./pages/partner/PartnerDelegatedJobDetail'));
const PartnerMySubmissions = React.lazy(() => import('./pages/partner/PartnerMySubmissions'));
const AdminExternalJobs = React.lazy(() => import('./pages/admin/AdminExternalJobs'));
const AdminExternalJobDetail = React.lazy(() => import('./pages/admin/AdminExternalJobDetail'));
const AdminExternalJobsImport = React.lazy(() => import('./pages/admin/AdminExternalJobsImport'));
const SkillsAdmin = React.lazy(() => import('./pages/admin/SkillsAdmin'));
const CategoriesAdmin = React.lazy(() => import('./pages/admin/CategoriesAdmin'));
const JobCategoriesAdmin = React.lazy(() => import('./pages/admin/JobCategoriesAdmin'));

// Candidate Pages (lazy — authenticated routes; SEO crawlers never reach these)
const CandidateProfile = React.lazy(() => import('./pages/candidate/CandidateProfile'));
const NotificationSettings = React.lazy(() => import('./pages/settings/NotificationSettings'));
const PartnerProfile = React.lazy(() => import('./pages/partner/PartnerProfile'));
const MyApplications = React.lazy(() => import('./pages/candidate/MyApplications'));
const CandidateDashboard = React.lazy(() => import('./pages/candidate/CandidateDashboard'));
const SavedJobs = React.lazy(() => import('./pages/candidate/SavedJobs'));

// Recruiter Pages (lazy — authenticated routes; SEO crawlers never reach these)
const ApplicationManagement = React.lazy(() => import('./pages/recruiter/ApplicationManagement'));
const MyJobs = React.lazy(() => import('./pages/recruiter/MyJobs'));
const RecruiterProfile = React.lazy(() => import('./pages/recruiter/RecruiterProfile'));
const RecruiterDashboard = React.lazy(() => import('./pages/recruiter/RecruiterDashboard'));
const TalentSearch = React.lazy(() => import('./pages/recruiter/TalentSearch'));
const CandidateProfileView = React.lazy(() => import('./pages/recruiter/CandidateProfileView'));
const RecruiterBuckets = React.lazy(() => import('./pages/recruiter/RecruiterBuckets'));
const PartnerDashboard = React.lazy(() => import('./pages/partner/PartnerDashboard'));
const PartnerUploads = React.lazy(() => import('./pages/partner/PartnerUploads'));
const PartnerAffiliateLinks = React.lazy(() => import('./pages/partner/PartnerAffiliateLinks'));

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <Loading fullScreen text="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const JobAdminRestricted: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isJobManagementAdmin } = useAuth();

  if (isJobManagementAdmin) {
    return <Navigate to="/admin/jd-review" replace />;
  }

  return <>{children}</>;
};

// Footer Component
const Footer: React.FC = () => {
  const [modalSrc, setModalSrc] = React.useState<string | null>(null);
  const closeModal = () => setModalSrc(null);

  return (
    <footer className="footer">
      <div className="container">
        © {new Date().getFullYear()} talixjobs powered by Nexofyre Technology Pvt Ltd •
        <button
          type="button"
          className="btn btn-link"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={() => setModalSrc('/faq.html')}
        >
          FAQ
        </button>
        •
        <button
          type="button"
          className="btn btn-link"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={() => setModalSrc('/privacy.html')}
        >
          Privacy
        </button>
        •
        <button
          type="button"
          className="btn btn-link"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={() => setModalSrc('/terms.html')}
        >
          Terms
        </button>
        •
        <button
          type="button"
          className="btn btn-link"
          style={{ marginLeft: '8px', padding: 0 }}
          onClick={() => {
            window.location.href = '/pricing';
          }}
        >
          Pricing
        </button>
      </div>
      {modalSrc && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
          onClick={closeModal}
        >
          <div
            className="modal modal-large"
            style={{ width: 'min(960px, 96vw)', height: 'min(80vh, 900px)', padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{modalSrc.includes('privacy') ? 'Privacy Policy' : modalSrc.includes('faq') ? 'FAQ' : 'Terms of Use'}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body" style={{ height: 'calc(100% - 64px)', padding: 0 }}>
              <iframe
                title={modalSrc}
                src={modalSrc}
                style={{ border: 0, width: '100%', height: '100%', background: 'white' }}
              />
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

// Main App Content
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, user, updateUser } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [limitModal, setLimitModal] = React.useState<any | null>(null);
  const [resumeUploadError, setResumeUploadError] = React.useState<string | null>(null);
  const [resumeUploading, setResumeUploading] = React.useState(false);
  const resumeInputRef = React.useRef<HTMLInputElement | null>(null);
  const shouldShowResumeReminder = user?.role === 'candidate' && !user?.resume_path;
  const isPortalV2User = user?.role === 'candidate' || user?.role === 'recruiter';
  const PROTECTED_PATH_PREFIXES = [
    '/candidate',
    '/recruiter',
    '/admin',
    '/partner',
    '/profile',
    '/applications',
    '/saved-jobs',
    '/help',
    '/change-password',
    '/notifications'
  ];
  const EXPLICIT_PUBLIC_ROUTES = [
    '/',
    '/jobs',
    '/login',
    '/register',
    '/forgot-password',
    '/verify-email-required',
    '/pricing',
    '/talk-to-sales',
    '/payment/success',
    '/payment/failed'
  ];
  const isExplicitPublic =
    EXPLICIT_PUBLIC_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.startsWith('/verify-email/');
  const isProtectedPath = PROTECTED_PATH_PREFIXES.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );
  const isPreLoginRoute = isExplicitPublic || !isProtectedPath;
  const shouldApplyPortalV2Theme = isPortalV2User || isPreLoginRoute;
  const portalRoleClass = user?.role === 'candidate' ? 'portal-v2-candidate' : user?.role === 'recruiter' ? 'portal-v2-recruiter' : '';
  const isEnterpriseUser = user?.role === 'admin' || user?.role === 'partner';
  const ActiveSidebar = isPortalV2User ? PortalSidebar : Sidebar;
  const ActiveHeader = isPortalV2User ? PortalHeader : Header;

  React.useEffect(() => {
    const onLimitReached = (event: Event) => {
      const custom = event as CustomEvent;
      setLimitModal(custom.detail || {});
    };
    window.addEventListener('pricing-limit-reached', onLimitReached as EventListener);
    return () => window.removeEventListener('pricing-limit-reached', onLimitReached as EventListener);
  }, []);

  const handleBannerUploadClick = () => {
    setResumeUploadError(null);
    resumeInputRef.current?.click();
  };

  const handleBannerResumeSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setResumeUploadError('Only PDF, DOC, or DOCX files are allowed.');
      return;
    }
    if (file.size > 16 * 1024 * 1024) {
      setResumeUploadError('Resume file must be less than 16MB.');
      return;
    }

    try {
      setResumeUploading(true);
      setResumeUploadError(null);
      const response = await uploadAPI.uploadResume(file);
      if (response.user) {
        updateUser(response.user);
      }
    } catch (err: any) {
      setResumeUploadError(err?.response?.data?.error || err?.message || 'Failed to upload resume.');
    } finally {
      setResumeUploading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen text="Initializing..." />;
  }

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'expanded' : ''} ${shouldApplyPortalV2Theme ? `portal-v2 ${portalRoleClass}` : ''} ${isEnterpriseUser ? 'portal-enterprise' : ''}`}>
      <ActiveSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((prev) => !prev)}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
      />
      {mobileSidebarOpen && <button className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} aria-label="Close navigation menu" />}
      <div className={`app-shell__content ${shouldApplyPortalV2Theme ? 'portal-v2-content' : ''}`}>
        <ActiveHeader onMenuClick={() => setMobileSidebarOpen((prev) => !prev)} />
        <main>
          {shouldShowResumeReminder && (
            <div className="resume-reminder-banner">
              <div className="alert warning" style={{ margin: 0 }}>
                Resume not uploaded yet. Upload your resume to continue applying for jobs and to become visible to recruiters in talent search.
                <button
                  type="button"
                  className="btn ghost"
                  style={{ marginLeft: '12px', padding: '6px 10px' }}
                  onClick={handleBannerUploadClick}
                  disabled={resumeUploading}
                >
                  {resumeUploading ? 'Uploading...' : 'Upload Resume'}
                </button>
                <input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={handleBannerResumeSelected}
                />
                {resumeUploadError && (
                  <div className="alert error" style={{ marginTop: '8px' }}>
                    {resumeUploadError}
                  </div>
                )}
              </div>
            </div>
          )}
          <React.Suspense fallback={<Loading text="Loading..." />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomeGate />} />
          <Route path="/jobs" element={<JobsGate />} />
          {/* SEO landing pages by location: /jobs/in/bengaluru, /jobs/in/remote, etc.
              Must come BEFORE /jobs/:slug otherwise the slug-based JobDetail would catch it. */}
          <Route path="/jobs/in/:city" element={<LocationLanding />} />
          {/* SEO landing pages by role: /jobs/role/python-developer, etc. */}
          <Route path="/jobs/role/:role" element={<RoleLanding />} />
          {/* SEO landing pages by skill: /jobs/skill/react, /jobs/skill/python, etc. */}
          <Route path="/jobs/skill/:skill" element={<SkillLanding />} />
          {/* SEO landing pages by category */}
          <Route path="/jobs/category/:parent" element={<CategoryParentLanding />} />
          <Route path="/jobs/category/:parent/:child" element={<CategoryChildLanding />} />
          {/* Public company profile pages — slug + ID resolution like /jobs/<slug> */}
          <Route path="/companies/:slug" element={<CompanyProfile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/verify-email-required" element={<VerifyEmailRequired />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/talk-to-sales" element={<TalkToSales />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failed" element={<PaymentFailed />} />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Any authenticated user */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['candidate']}>
                <CandidateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute allowedRoles={['candidate']}>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute allowedRoles={['candidate', 'recruiter', 'admin', 'partner']}>
                <Help />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute allowedRoles={['candidate', 'recruiter', 'admin', 'partner']}>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* Candidate Routes */}
          <Route 
            path="/candidate/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['candidate']}>
                <CandidateDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/applications" 
            element={
              <ProtectedRoute allowedRoles={['candidate']}>
                <MyApplications />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/saved-jobs"
            element={
              <ProtectedRoute allowedRoles={['candidate']}>
                <SavedJobs />
              </ProtectedRoute>
            }
          />

          {/* Recruiter Routes */}
          <Route 
            path="/recruiter/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/recruiter/jobs" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <MyJobs />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/recruiter/applications" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <ApplicationManagement />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/recruiter/talent" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <TalentSearch />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/recruiter/candidates/:id" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <CandidateProfileView />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/recruiter/profile" 
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterProfile />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/recruiter/buckets"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterBuckets />
              </ProtectedRoute>
            }
          />

          <Route
            path="/recruiter/org-members"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterOrgMembers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/org/jobs"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterOrgJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/org/invites"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterOrgInvites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/org/partners"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <OrgPartnerAllowlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/delegated-jobs"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterDelegatedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/delegated-jobs/:jobId"
            element={
              <ProtectedRoute allowedRoles={['recruiter']}>
                <RecruiterDelegatedJobSubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/delegated-jobs"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerDelegatedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/delegated-jobs/my-submissions"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerMySubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/delegated-jobs/:jobId"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerDelegatedJobDetail />
              </ProtectedRoute>
            }
          />

          {/* Partner Routes */}
          <Route
            path="/partner/profile"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner/dashboard"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner/uploads"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerUploads />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner/affiliate-links"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerAffiliateLinks />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminDashboard />
                </JobAdminRestricted>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <UserManagement />
                </JobAdminRestricted>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/users/:id" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminUserDetail />
                </JobAdminRestricted>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/jobs" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <JobManagement />
                </JobAdminRestricted>
              </ProtectedRoute>
            } 
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminSettings />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/org-caps"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminOrgCaps />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/org-cap"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminOrgCapSettings />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminActivity />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminSupport />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminBilling />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminBillingDashboard />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/coupons"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminCouponManagement />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/coupons/:couponId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminCouponManagement />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/kyc-reviews"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminKycReviews />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partner-uploads"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminPartnerUploads />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partner-uploads/approved"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminApprovedPartnerProfiles />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/affiliate-referrals"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminAffiliateReferrals />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/partners"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminBillingPartners />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/invoices"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminBillingInvoices />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/payments"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminPaymentGateway />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/payment-mappings"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminPaymentMappings />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/pricing-controls"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminPricingControls />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/recruiter-exceptions"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminRecruiterExceptions />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing/org-admins"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminOrgAdmins />
                </JobAdminRestricted>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/admin/jobs/:id" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobAdminRestricted>
                  <AdminJobDetail />
                </JobAdminRestricted>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/jd-dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JDReviewDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/jd-review" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JDReviewList />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/admin/jd-review/:jobId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JDReviewDetail />
              </ProtectedRoute>
            }
          />

          <Route path="/admin/skills-moderation" element={<Navigate to="/admin/skills" replace />} />
          <Route
            path="/admin/skills"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SkillsAdmin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CategoriesAdmin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/job-categories"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <JobCategoriesAdmin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/external-jobs"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminExternalJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/external-jobs/import"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminExternalJobsImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/external-jobs/:jobId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminExternalJobDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminProfile />
              </ProtectedRoute>
            } 
          />

          {/* Dynamic Dashboard Route */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            } 
          />

          {/* Job Detail Route */}
          {/* Accept both numeric IDs (legacy) and SEO slugs ending with -<id>. JobDetail parses both. */}
          <Route path="/jobs/:slug" element={<JobDetail />} />

          {/* 404 Route */}
          <Route 
            path="*" 
            element={
              <div className="container center">
                <div className="card" style={{ textAlign: 'center' }}>
                  <h1 className="h1">404</h1>
                  <p className="muted">Page not found</p>
                  <a href="/" className="btn">Go Home</a>
                </div>
              </div>
            } 
          />
          </Routes>
          </React.Suspense>
        </main>
        {limitModal && (
          <div className="modal-overlay" onClick={() => setLimitModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Free Plan Limit Reached</h3>
                <button className="modal-close" onClick={() => setLimitModal(null)} aria-label="Close">&times;</button>
              </div>
              <div className="modal-body">
                <p className="muted">
                  {limitModal?.error || 'You have reached your current free-plan usage limit.'}
                </p>
                {typeof limitModal?.used === 'number' && typeof limitModal?.limit === 'number' && (
                  <p className="muted">Used: {limitModal.used} / {limitModal.limit}</p>
                )}
                <p className="muted">You can subscribe now or continue on the free plan.</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    className="btn"
                    onClick={() => {
                      setLimitModal(null);
                      navigate('/pricing');
                    }}
                  >
                    Subscribe
                  </button>
                  <button className="btn ghost" onClick={() => setLimitModal(null)}>
                    Continue Free
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <CookieNotice />
        <Footer />
      </div>
    </div>
  );
};

// Dashboard Redirect Component
const DashboardRedirect: React.FC = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case 'admin':
      if ((user.admin_title || '').toLowerCase() === 'job management admin') {
        return <Navigate to="/admin/jd-dashboard" replace />;
      }
      if ((user.admin_title || '').toLowerCase() === 'billing admin') {
        return <Navigate to="/admin/billing" replace />;
      }
      return <Navigate to="/admin/dashboard" replace />;
    case 'recruiter':
      return <Navigate to="/recruiter/dashboard" replace />;
    case 'candidate':
      return <Navigate to="/candidate/dashboard" replace />;
    case 'partner':
      return <Navigate to="/partner/dashboard" replace />;
    default:
      return <Navigate to="/" replace />;
  }
};

const HomeGate: React.FC = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Home />;
  }

  switch (user.role) {
    case 'admin':
      if ((user.admin_title || '').toLowerCase() === 'job management admin') {
        return <Navigate to="/admin/jd-dashboard" replace />;
      }
      if ((user.admin_title || '').toLowerCase() === 'billing admin') {
        return <Navigate to="/admin/billing" replace />;
      }
      return <Navigate to="/admin/dashboard" replace />;
    case 'recruiter':
      return <Navigate to="/recruiter/dashboard" replace />;
    case 'candidate':
      return <Navigate to="/candidate/dashboard" replace />;
    case 'partner':
      return <Navigate to="/partner/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Jobs Route Gate for recruiters
const JobsGate: React.FC = () => {
  const { isRecruiter } = useAuth();

  if (isRecruiter) {
    return <Navigate to="/recruiter/dashboard" replace />;
  }

  return <Jobs />;
};

// Main App Component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
